<template>
	<!-- eslint-disable max-lines-->
	<Modal max-width="1000px">
		<template #modal-title>
			<IconButton
				v-if="showImportView"
				data-testid="back-button"
				@click="showImportView = false"
			>
				mdi-arrow-left
			</IconButton>
			<span class="title">
				{{ showImportView ? 'Import System Configuration' : 'Create New System' }}
			</span>
			<v-spacer />
			<IconButton
				data-testid="close-button"
				@click="$emit('close')"
			>
				mdi-close
			</IconButton>
		</template>

		<template #modal-content>
			<!-- Options Selection View -->
			<div
				v-if="!showImportView"
				class="system-creation-options"
			>
				<p class="subtitle-1 text--secondary mb-6 text-center">
					Choose how you'd like to set up your new system
				</p>
				<v-row class="justify-center align-stretch">
					<v-col
						cols="12"
						sm="6"
						md="4"
						class="d-flex"
					>
						<OverviewCard
							title="Use Template"
							description="Start with a pre-configured template for popular systems like HubSpot, Mailchimp, or Salesforce."
							icon="mdi-file-document-multiple"
							use-grey
							data-testid="template-option"
							@overview="handleTemplateSelect"
						/>
					</v-col>

					<v-col
						cols="12"
						sm="6"
						md="4"
						class="d-flex"
					>
						<OverviewCard
							title="Custom Setup"
							description="Build your system from scratch with complete control over all settings and configurations."
							icon="mdi-cog"
							use-grey
							data-testid="custom-option"
							@overview="handleCustomSelect"
						/>
					</v-col>

					<v-col
						cols="12"
						sm="6"
						md="4"
						class="d-flex"
					>
						<OverviewCard
							title="Import Configuration"
							description="Import an existing system configuration using JSON format for quick setup."
							icon="mdi-file-import"
							use-grey
							data-testid="import-option"
							@overview="handleImportSelect"
						/>
					</v-col>
				</v-row>
			</div>

			<!-- Import Configuration View -->
			<div
				v-else
				class="import-configuration-view"
			>
				<p class="subtitle-1 text--secondary mb-6">
					Upload a JSON configuration file exported from another system to quickly recreate your integration setup. The file will be validated and used to create your system and any included services.
				</p>

				<!-- File Upload Section -->
				<div class="file-upload-section mb-6">
					<FileInput
						label="Select JSON Configuration File"
						accept=".json"
						:disabled="isImporting"
						select-file-only
						@selectFile="handleFileUpload"
					/>
				</div>

				<!-- Import Preview Section -->
				<div
					v-if="importJsonData && isValidImportData"
					class="import-preview-section mb-4"
				>
					<h6 class="subtitle-2 mb-3">
						Import Preview:
					</h6>

					<!-- System Info -->
					<div class="preview-item system-preview">
						<strong>
							System:
						</strong>
						{{ systemPreview.name }}
					</div>

					<!-- Services List -->
					<div
						v-if="servicesPreview.length > 0"
						class="services-preview"
					>
						<div class="text-body-2 text--secondary mb-2">
							<strong>
								{{ servicesPreview.length }} Integration {{ servicesPreview.length !== 1 ? 's' : '' }}:
							</strong>
						</div>
						<div class="services-list">
							<div
								v-for="service in servicesPreview"
								:key="service.name"
								class="service-item"
							>
								• {{ service.name }}
							</div>
						</div>
					</div>
				</div>

				<div
					v-if="importValidationError"
					class="import-error mt-3"
				>
					<v-alert
						type="error"
						dense
						outlined
					>
						<pre class="import-error__pre">{{ importValidationError }}</pre>
					</v-alert>
				</div>
			</div>
		</template>
		<template
			v-if="showImportView"
			#modal-footer
		>
			<secondary-action-button
				class="mr-3"
				data-testid="cancel-import-button"
				@click="cancelImport"
			>
				Cancel
			</secondary-action-button>
			<v-spacer />
			<primary-action-button
				:disabled="!isValidImportData || isImporting"
				:loading="isImporting"
				data-testid="create-from-import-button"
				@click="createFromImport"
			>
				Create from Import
			</primary-action-button>
		</template>
	</Modal>
</template>

<script>
/* eslint-disable max-lines */
import Modal from '../../../../../shared/components/modal.vue'
import IconButton from '../../../../../shared/components/icon-button.vue'
import OverviewCard from '../../../../../shared/components/overview-card.vue'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../shared/components/secondary-action-button.vue'
import FileInput from '../../../../../shared/components/file-input.vue'
import { validateImportData } from './system-integration-validator.js'
import { postSystemIntegration, postIntegrationService } from '../../../../../shared/utils/api/connector-service.js'
import { appendJsonErrorContext } from '../../../../shared/utils/json-error-formatting.js'
export default {
	name: 'system-creation-options-modal',
	components: {
		Modal,
		IconButton,
		OverviewCard,
		PrimaryActionButton,
		SecondaryActionButton,
		FileInput
	},
	data () {
		return {
			showImportView: false,
			importJsonData: '',
			selectedFile: null,
			isImporting: false,
			isValidImportData: false,
			importValidationError: null
		}
	},
	computed: {
		systemPreview () {
			if (!this.importJsonData || !this.isValidImportData) return {}

			try {
				const parsedData = JSON.parse(this.importJsonData)
				const systemData = parsedData.systemIntegration || parsedData

				return {
					name: systemData.integrationSystemName || 'Unnamed System',
					endpointAddress: systemData.endpointAddress,
					notes: systemData.notes,
					enabled: systemData.enabled
				}
			} catch (error) {
				return {}
			}
		},
		servicesPreview () {
			if (!this.importJsonData || !this.isValidImportData) return []

			try {
				const parsedData = JSON.parse(this.importJsonData)

				if (parsedData.integrationServices && Array.isArray(parsedData.integrationServices)) {
					return parsedData.integrationServices.map(service => ({
						name: service.name || 'Unnamed Service',
						isEnabled: service.isEnabled || false,
						httpMethod: service.httpMethod,
						requestUrl: service.requestUrl
					}))
				}

				return []
			} catch (error) {
				return []
			}
		}
	},
	methods: {
		handleTemplateSelect () {
			this.$emit('select-template')
		},
		handleCustomSelect () {
			this.$emit('select-custom')
		},
		handleImportSelect () {
			this.showImportView = true
		},
		handleFileUpload (file) {
			if (!file) {
				return
			}

			// Validate file type
			if (!file.name.toLowerCase().endsWith('.json')) {
				this.importValidationError = 'Please select a valid JSON file (.json extension required)'
				this.selectedFile = null
				this.isValidImportData = false
				return
			}

			// Read file content
			const reader = new FileReader()
			reader.onload = e => {
				try {
					const content = e.target.result
					// Validate that it's valid JSON by trying to parse it
					JSON.parse(content)
					// If successful, set the content and validate
					this.importJsonData = content
					this.validateImportData()
				} catch (error) {
					this.importValidationError = 'Invalid JSON file: ' + error.message
					this.selectedFile = null
					this.importJsonData = ''
					this.isValidImportData = false
				}
			}
			reader.onerror = () => {
				this.importValidationError = 'Error reading file. Please try again.'
				this.selectedFile = null
				this.isValidImportData = false
			}
			reader.readAsText(file)
		},
		validateImportData () {
			const result = validateImportData(this.importJsonData)

			this.isValidImportData = result.isValid
			this.importValidationError = result.error ? appendJsonErrorContext(result.error, this.importJsonData) : null
		},
		async createFromImport () {
			if (!this.isValidImportData) return

			this.isImporting = true

			try {
				const configData = JSON.parse(this.importJsonData)

				// Create the system integration first
				const systemResponse = await postSystemIntegration(configData.systemIntegration || configData)

				// Check if we have integration services to import
				if (configData.integrationServices && Array.isArray(configData.integrationServices)) {
					// Get the system integration ID from the response
					const systemIntegrationId = systemResponse.data?.newSystemIntegrationId

					if (!systemIntegrationId) {
						throw new Error('Failed to retrieve system ID from response')
					}

					// Import each integration service
					for (const service of configData.integrationServices) {
						const serviceData = {
							...service,
							integrationId: systemIntegrationId,
							requestBody: typeof service.requestBody === 'string'
								? service.requestBody
								: JSON.stringify(service.requestBody, null, 2)
						}

						try {
							await postIntegrationService(serviceData)
						} catch (serviceError) {
							console.error(`Failed to import service "${service.name}":`, serviceError)
							// Continue with other services even if one fails
						}
					}
				}

				this.$emit('import-success')
			} catch (error) {
				this.importValidationError = appendJsonErrorContext('Failed to process configuration: ' + error.message, this.importJsonData, error)
			} finally {
				this.isImporting = false
			}
		},
		cancelImport () {
			this.showImportView = false
			this.importJsonData = ''
			this.selectedFile = null
			this.isValidImportData = false
			this.importValidationError = null
		}
		// Removed local JSON error formatting helpers in favor of shared utility.
	}
}
</script>

<style scoped>
.system-creation-options {
  min-height: 200px;
}

.options-container {
  max-width: 100%;
}

.import-error {
	width: fit-content
}

.import-error__pre {
	margin: 0;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	font-size: 12px;
	line-height: 1.4;
	white-space: pre-wrap;
	max-width: 800px;
	overflow-x: auto;
}

.option-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 350px;
  max-width: 400px;
}

.option-wrapper >>> .overview-card {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.option-wrapper >>> .overview-card .v-card__text {
  flex-grow: 1;
}

.file-upload-section {
  border: 1px dashed var(--global-border-color);
  border-radius: var(--radius-sm, 4px);
  padding: 16px;
  background-color: var(--global-main-background-color);
}

.import-preview-section {
  border: 1px solid var(--global-border-color);
  border-radius: var(--radius-sm, 4px);
  padding: 16px;
  background-color: #f9f9f9;
}

.preview-item {
  margin-bottom: 16px;
}

.system-preview {
  padding-bottom: 12px;
  border-bottom: 1px solid var(--global-border-color);
}

.services-preview {
  margin-top: 12px;
}

.services-list {
  margin-left: 8px;
}

.service-item {
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

</style>
